import * as THREE from 'three';
import { onGpu } from './gpu';
import { DECAL_LAYER } from './decals';

/**
 * 눈에 남는 발자국.
 *
 * ## 왜 필요했나
 *
 * 20cm 쌓인 눈밭인데 표면이 **어느 컷에서도 완전히 균일**했다. 눈을 쌓아 놓고
 * 그 위를 걸어도 아무 일이 안 일어나니, 눈은 「깊이가 있는 것」이 아니라
 * 바닥에 칠한 흰색이었다. 길가 둔덕(`berm`)을 만든 것과 같은 이유다 —
 * 눈이 몇 cm 쌓였는지는 **눈을 치운 자리**와 **밟은 자리**가 말한다.
 *
 * 살인 사건 마을이라 이건 분위기 이상이다. 눈 위의 발자국은 그 자체로 단서다.
 *
 * ## 지형을 안 판다
 *
 * 제대로 하려면 눈 높이 필드를 만들고 발이 닿은 자리를 눌러야 하는데, 이 마을의
 * 지면은 **700m 짜리 평면 한 장**(`outside.ts`)이라 누를 필드가 없다. 필드를
 * 새로 만들면 지면·둔덕·비탈·언덕이 전부 그 필드를 따라야 하고, 그건 이 장면을
 * 통째로 다시 짜는 일이다.
 *
 * 대신 **자국을 얹는다.** 밤 씬에서 파인 자국은 결국 (1) 눌려서 어두워진 바닥과
 * (2) 밀려 올라와 밝아진 테두리, 둘로 보인다. 그 둘을 텍스처 한 장에 굽고
 * 바닥에 눕히면, 2cm 띄운 납작한 판이 파인 구멍으로 읽힌다. `decals.ts` 가
 * 접지 그림자와 빛 웅덩이에 이미 쓰고 있는 수법이다.
 *
 * ## 한 번에 그린다
 *
 * 자국은 수백 장이 되므로 `InstancedMesh` 한 덩어리다. 오래된 것부터 덮어쓰는
 * 고리 버퍼라 메모리도 draw call 도 늘지 않는다. 흐려지는 것은 정점 알파가
 * 아니라 **인스턴스마다 하나씩 붙인 `aFade`** 가 한다 — 눈이 다시 덮는 것이다.
 */

/** 동시에 남아 있는 자국 수. 보폭 0.72m 니 약 230m 어치다 */
const MAX = 640;
/** 자국이 눈에 완전히 덮이기까지(초). 뒤쪽 45% 구간에서 흐려진다 */
const LIFE = 150;
/** 보폭 */
const STRIDE = 0.72;
/** 좌우 발 간격의 반 */
const HALF = 0.105;
/** 자국 판 크기(밀려 올라온 테두리까지 포함) */
const LEN = 0.44, WID = 0.28;

/**
 * 장화 바닥 한 짝.
 *
 * 세 겹으로 굽는다 — 크게 번진 **밝은 테두리**(밀려 올라온 눈), 그 위에 얹는
 * **어두운 바닥**(눌려서 그늘진 자리), 마지막에 **골 무늬**. 알파는 따로 안
 * 그린다. 그린 것의 합집합이 곧 알파고, 번짐이 그대로 가장자리 페이드가 된다.
 *
 * 같은 그림을 `bumpMap` 으로도 쓴다. 밝은 테두리는 솟고 어두운 안쪽은 꺼지므로
 * 요철 방향이 저절로 맞는다 — 가로등 빛이 자국 테두리에 걸린다.
 */
function bootTexture(): THREE.CanvasTexture {
  const W = 128, H = 200;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d')!;

  /**
   * 앞볼 + 허리 + 뒤꿈치. `k` 는 바깥으로 부풀리는 px.
   *
   * **타원 두 개로는 장화가 안 나온다** — 첫 판이 정확히 그거였고, 확대해 보니
   * 전구(電球) 모양이었다. 큰 원 하나에 작은 원 하나가 붙은 실루엣은 사람 발이
   * 아니다. 장화는 앞볼이 네모에 가깝게 넓고, 허리가 잘록하고, 뒤꿈치가 다시
   * 각진 블록이다. 그래서 둥근 사각형 세 개를 겹쳐 합집합으로 만든다.
   */
  const sole = (k: number) => {
    g.beginPath();
    // 앞볼 — 발끝만 둥글다
    g.roundRect(W * 0.24 - k, H * 0.08 - k, W * 0.52 + 2 * k, H * 0.36 + 2 * k,
      [W * 0.25 + k, W * 0.25 + k, W * 0.08 + k, W * 0.08 + k]);
    // 허리 — 살짝 잘록할 뿐이다. 너무 좁히면 아령(啞鈴)이 된다(두 번째 판이 그랬다)
    g.roundRect(W * 0.32 - k, H * 0.41 - k, W * 0.36 + 2 * k, H * 0.19 + 2 * k, W * 0.04 + k);
    // 뒤꿈치 — 앞볼보다 짧고 각진 블록
    g.roundRect(W * 0.27 - k, H * 0.57 - k, W * 0.46 + 2 * k, H * 0.33 + 2 * k,
      [W * 0.07 + k, W * 0.07 + k, W * 0.20 + k, W * 0.20 + k]);
    g.fill();
  };

  /**
   * 밀려 올라온 눈.
   *
   * **테두리가 너무 밝고 두꺼우면 자국이 아니라 빛나는 얼룩이 된다** — 첫 판이
   * 8px 부풀림에 `#dde7f4` 였고, 확대하니 발자국마다 흰 후광이 붙어 스티커처럼
   * 보였다. 부풀림을 절반으로 줄이고, 눈보다 조금만 밝은 색을 알파 0.72 로
   * 얹는다. 테두리는 **있는 줄 모르게 있어야** 안쪽이 파여 보인다.
   */
  g.globalAlpha = 0.6;
  g.filter = 'blur(4px)';
  g.fillStyle = '#c3d0e2';
  sole(3.5);
  /**
   * 눌린 바닥. 밤의 눈 구덩이는 검은 게 아니라 하늘빛만 남아 **푸르게** 죽는다.
   * 주변 눈(화면에서 대략 130/126/141)보다 확실히 어두워야 구멍으로 읽힌다 —
   * 첫 판은 `#71829b` 라 주변과 값이 비슷해 평평했다.
   */
  g.globalAlpha = 1;
  g.filter = 'blur(2.5px)';
  g.fillStyle = '#596882';
  sole(0);
  // 골. 사람이 지나간 자국인지 짐승인지를 가르는 건 결국 이 무늬다
  g.filter = 'none';
  g.save();
  g.beginPath();
  g.roundRect(W * 0.24 + 2, H * 0.08 + 2, W * 0.52 - 4, H * 0.36 - 4, W * 0.20);
  g.roundRect(W * 0.27 + 2, H * 0.57 + 2, W * 0.46 - 4, H * 0.33 - 4, W * 0.16);
  g.clip();
  g.fillStyle = '#7f8fa8';
  // 가로 골 — 앞볼 다섯, 뒤꿈치 셋
  for (let i = 0; i < 5; i++) g.fillRect(0, H * 0.11 + i * H * 0.065, W, H * 0.022);
  for (let i = 0; i < 3; i++) g.fillRect(0, H * 0.62 + i * H * 0.082, W, H * 0.026);
  // 세로 골 하나 — 이게 있어야 「블록 창」으로 읽힌다
  g.fillRect(W * 0.49, 0, W * 0.022, H);
  g.restore();

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/** 자국 하나를 눕힌 사각형. +Y 를 보고, 발끝이 -Z 다 */
function printGeo(): THREE.BufferGeometry {
  const hw = WID / 2, hl = LEN / 2;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(
    [-hw, 0, hl, hw, 0, hl, hw, 0, -hl, -hw, 0, -hl], 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute([0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0], 3));
  // CanvasTexture 는 flipY 라 v=1 이 캔버스 위쪽(발끝)이다
  g.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  return g;
}

/** 걷는 사람 하나의 보폭 상태 */
interface Walker {
  x: number;
  z: number;
  /** 다음 자국까지 남은 거리 */
  left: number;
  /** 왼발/오른발 */
  side: 1 | -1;
  /** 직전 자국의 높이. 경사를 재는 데 쓴다 */
  y: number;
  seen: number;
}

export class SnowTracks {
  private mesh: THREE.InstancedMesh;
  private fade: THREE.InstancedBufferAttribute;
  /** 각 슬롯이 찍힌 시각. 아직 안 쓴 슬롯은 -1 */
  private born = new Float32Array(MAX).fill(-1);
  private next = 0;
  private walkers = new Map<string, Walker>();
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private e = new THREE.Euler();
  private v = new THREE.Vector3();
  private one = new THREE.Vector3(1, 1, 1);
  private now = 0;

  constructor(parent: THREE.Object3D) {
    const geo = printGeo();
    const fadeArr = new Float32Array(MAX);
    this.fade = new THREE.InstancedBufferAttribute(fadeArr, 1);
    this.fade.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aFade', this.fade);

    const tex = bootTexture();
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      bumpMap: tex,
      bumpScale: 0.55,
      roughness: 0.95,
      transparent: true,
      depthWrite: false,
      // 바닥과 거의 같은 평면이다. 안 당기면 깜빡인다
      polygonOffset: true,
      polygonOffsetFactor: -3,
      polygonOffsetUnits: -3,
    });
    // 인스턴스마다 알파를 따로 준다. `instanceColor` 는 RGB 뿐이라 못 쓴다
    mat.onBeforeCompile = (sh) => {
      sh.vertexShader = 'attribute float aFade;\nvarying float vFade;\n' + sh.vertexShader.replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\n vFade = aFade;',
      );
      sh.fragmentShader = 'varying float vFade;\n' + sh.fragmentShader.replace(
        '#include <dithering_fragment>',
        '#include <dithering_fragment>\n gl_FragColor.a *= vFade;',
      );
    };
    mat.customProgramCacheKey = () => 'snowTrack';
    onGpu(mat, (n, m) => n.trackFade(m));   // WebGPU 판(gpuNodes.ts)

    this.mesh = new THREE.InstancedMesh(geo, mat, MAX);
    this.mesh.name = '발자국';
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    // 데칼은 GTAO 에 보이면 안 된다 — `decals.ts` 의 기록 참조.
    // 바닥에서 2cm 뜬 판이 깊이 버퍼에 잡히면 그 자리가 새까맣게 눌린다
    this.mesh.layers.set(DECAL_LAYER);
    this.mesh.renderOrder = 3;
    parent.add(this.mesh);
  }

  /**
   * 걷는 사람의 현재 위치를 넘긴다. 보폭만큼 나아갈 때마다 자국이 하나 찍힌다.
   *
   * @param id  사람 구분자. 나와 원격 아바타가 각자 보폭을 센다
   * @param y   **발이 딛고 있는 높이.** 카메라 높이가 아니다
   * @param mark 눈 위인가. 마루·실내에서는 `false` — 보폭은 계속 세되 안 찍는다.
   *   여기서 그냥 안 부르면 마루를 건너간 만큼이 다음 걸음에 몰려서, 마당으로
   *   내려서는 순간 엉뚱한 자리에 한 짝이 찍힌다
   */
  follow(id: string, x: number, y: number, z: number, yaw: number, mark = true) {
    const w = this.walkers.get(id);
    if (!w) {
      this.walkers.set(id, { x, y, z, left: STRIDE * 0.5, side: 1, seen: this.now });
      return;
    }
    w.seen = this.now;
    const dx = x - w.x, dz = z - w.z;
    const d = Math.hypot(dx, dz);
    w.x = x;
    w.z = z;
    if (d < 1e-4) return;
    /**
     * **순간이동에는 자국이 안 남는다.** `__dbg.tp` 나 리스폰으로 한 프레임에
     * 수십 m 를 건너뛰면, 그 사이를 보폭으로 채우는 게 아니라 그냥 끊는다.
     * 안 끊으면 마을을 가로지르는 직선 자국이 생긴다
     */
    if (d > 2.5) {
      w.left = STRIDE * 0.5;
      w.y = y;
      return;
    }
    w.left -= d;
    if (w.left > 0) return;
    w.left += STRIDE;
    // 오르막이면 자국도 기울어야 한다. 지형을 다시 물어보지 않고
    // 직전 자국과의 높이차로 잰다 — 램프 위에서 판이 튀어나오는 걸 막는다
    const pitch = Math.atan2(y - w.y, Math.max(0.05, STRIDE));
    w.y = y;
    w.side = w.side === 1 ? -1 : 1;
    if (!mark) return;
    this.stamp(x + Math.cos(yaw) * HALF * w.side, y, z - Math.sin(yaw) * HALF * w.side, yaw, pitch);
  }

  /** 자국 하나를 고리 버퍼에 굽는다 */
  private stamp(x: number, y: number, z: number, yaw: number, pitch: number) {
    const i = this.next;
    this.next = (this.next + 1) % MAX;
    this.born[i] = this.now;
    // 바퀴자국 데칼(y+0.015)보다 위. 밑으로 깔면 길 위에서 자국이 사라진다
    this.v.set(x, y + 0.022, z);
    this.e.set(pitch, yaw, 0, 'YXZ');
    this.q.setFromEuler(this.e);
    this.m.compose(this.v, this.q, this.one);
    this.mesh.setMatrixAt(i, this.m);
    this.fade.setX(i, 1);
    if (this.mesh.count < MAX) this.mesh.count = Math.max(this.mesh.count, i + 1);
    this.mesh.instanceMatrix.needsUpdate = true;
    this.fade.needsUpdate = true;
  }

  /** 매 프레임. 자국을 눈이 덮어 간다 */
  update(t: number) {
    this.now = t;
    let dirty = false;
    for (let i = 0; i < this.mesh.count; i++) {
      const b = this.born[i];
      if (b < 0) continue;
      const age = (t - b) / LIFE;
      // 앞 55% 는 그대로 두고 뒤에서만 흐려진다. 처음부터 옅어지면
      // 「눈이 덮는 중」이 아니라 「자국이 원래 흐릿한」 것으로 보인다
      const f = age >= 1 ? 0 : 1 - Math.max(0, (age - 0.55) / 0.45) ** 1.4;
      if (Math.abs(f - this.fade.getX(i)) > 0.004) {
        this.fade.setX(i, f);
        dirty = true;
      }
      if (age >= 1) this.born[i] = -1;
    }
    if (dirty) this.fade.needsUpdate = true;
    // 나간 사람은 잊는다. 안 지우면 재입장한 사람이 옛 자리에서 이어 걷는다
    if (this.walkers.size > 1) {
      for (const [id, w] of this.walkers) if (t - w.seen > 3) this.walkers.delete(id);
    }
  }
}
